import { EndpointConfig, EndpointType } from 'utility/Request/Api/types';
import {
    baseLocationProvider,
    defaultBaseUrlProvider,
} from 'utility/Request/defaultBaseUrlProvider';

import { ApiEndpoint, EndpointTypeMap } from 'types/Request/endpoint';

export const ENDPOINT_CONFIG: EndpointConfig<EndpointTypeMap> = {
    [ApiEndpoint.CheckGpt]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/search/checkgpt',
        baseUrlProvider: defaultBaseUrlProvider,
        converterProvider: async () => {
            const module = await import('proto/Api/Search/CheckGpt');
            return {
                Request: module.CheckGptRequest,
                Response: module.CheckGptResponse,
            };
        },
    },
    [ApiEndpoint.CheckGptTrackingAnswerSubmitted]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/search/checkgpt/tracking/answer-submitted',
        baseUrlProvider: defaultBaseUrlProvider,
        converterProvider: async () => {
            const [module, successModule] = await Promise.all([
                import('proto/Api/Search/CheckGpt'),
                import('proto/Api/Success'),
            ]);

            return {
                Request: module.CheckGptTrackingAnswerSubmittedRequest,
                Response: successModule.SuccessResponse,
            };
        },
    },
    [ApiEndpoint.CheckGptTrackingAskSubmitted]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/search/checkgpt/tracking/ask-submitted',
        baseUrlProvider: defaultBaseUrlProvider,
        converterProvider: async () => {
            const [module, successModule] = await Promise.all([
                import('proto/Api/Search/CheckGpt'),
                import('proto/Api/Success'),
            ]);

            return {
                Request: module.CheckGptTrackingAskSubmittedRequest,
                Response: successModule.SuccessResponse,
            };
        },
    },
    [ApiEndpoint.CheckGptTrackingQuestionSubmitted]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/search/checkgpt/tracking/question-submitted',
        baseUrlProvider: defaultBaseUrlProvider,
        converterProvider: async () => {
            const [module, successModule] = await Promise.all([
                import('proto/Api/Search/CheckGpt'),
                import('proto/Api/Success'),
            ]);
            return {
                Request: module.CheckGptTrackingQuestionSubmittedRequest,
                Response: successModule.SuccessResponse,
            };
        },
    },
    [ApiEndpoint.CheckGptTrackingVoteSubmitted]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/search/checkgpt/tracking/vote-submitted',
        baseUrlProvider: defaultBaseUrlProvider,
        converterProvider: async () => {
            const [module, successModule] = await Promise.all([
                import('proto/Api/Search/CheckGpt'),
                import('proto/Api/Success'),
            ]);

            return {
                Request: module.CheckGptTrackingVoteSubmittedRequest,
                Response: successModule.SuccessResponse,
            };
        },
    },
    [ApiEndpoint.CheckGptTrackingStopSubmitted]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/search/checkgpt/tracking/stop-submitted',
        baseUrlProvider: defaultBaseUrlProvider,
        converterProvider: async () => {
            const [module, successModule] = await Promise.all([
                import('proto/Api/Search/CheckGpt'),
                import('proto/Api/Success'),
            ]);
            return {
                Request: module.CheckGptTrackingStopSubmittedRequest,
                Response: successModule.SuccessResponse,
            };
        },
    },
    [ApiEndpoint.CheckGptTrackingError]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/search/checkgpt/tracking/error',
        baseUrlProvider: defaultBaseUrlProvider,
        converterProvider: async () => {
            const [module, successModule] = await Promise.all([
                import('proto/Api/Search/CheckGpt'),
                import('proto/Api/Success'),
            ]);
            return {
                Request: module.CheckGptTrackingErrorRequest,
                Response: successModule.SuccessResponse,
            };
        },
    },
    [ApiEndpoint.HotelList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/hotel/list',
        converterProvider: async () => {
            const module = await import('proto/Api/Hotel/HotelList');
            return {
                Request: module.HotelListRequest,
                Response: module.HotelListResponse,
            };
        },
    },
    [ApiEndpoint.HotelContentSearch]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/hotel/content-search',
        converterProvider: async () => {
            const module = await import('proto/Api/Hotel/HotelContentSearch');
            return {
                Request: module.HotelContentSearchRequest,
                Response: module.HotelContentSearchResponse,
            };
        },
    },
    [ApiEndpoint.LastSearchesList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/user/last-searches/list',
        converterProvider: async () => {
            const module = await import('proto/Api/User/LastSearches');
            return {
                Request: module.LastSearchesListRequest,
                Response: module.LastSearchesListResponse,
            };
        },
    },
    [ApiEndpoint.LastSearchesAdd]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/user/last-searches/add',
        converterProvider: async () => {
            const [module, successModule] = await Promise.all([
                import('proto/Api/User/LastSearches'),
                import('proto/Api/Success'),
            ]);
            return {
                Request: module.LastSearchesAddRequest,
                Response: successModule.SuccessResponse,
            };
        },
    },
    [ApiEndpoint.ListPolling]: {
        type: EndpointType.Get,
    },
    [ApiEndpoint.UnitList]: {
        type: EndpointType.Get,
        url: '/suche/json/unit-carousel/:hotelId',
    },
    [ApiEndpoint.UnitDetail]: {
        type: EndpointType.Get,
        url: '/hib_json/hotel/:hotelId/units/:unitId',
    },
    [ApiEndpoint.RoomDetail]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/offer/room-detail',
        converterProvider: async () => {
            const module = await import('proto/Api/Offer/RoomDetail');
            return {
                Request: module.RoomDetailRequest,
                Response: module.RoomDetailResponse,
            };
        },
    },
    [ApiEndpoint.SemHotelList]: {
        type: EndpointType.Post,
        url: '/vergleiche/json/offers',
    },
    [ApiEndpoint.SemDestinationOverview]: {
        type: EndpointType.Post,
        url: '/vergleiche/json/destination-overview',
    },
    [ApiEndpoint.CsCodePrepaidVoucher]: {
        type: EndpointType.Post,
        url: '/json/cscodes/prepaid-voucher',
    },
    [ApiEndpoint.CsCodeUpdate]: {
        type: EndpointType.Post,
        url: '/jsonp/cscodes/get-updated-cs-content',
    },
    [ApiEndpoint.SmsLinkPrepaidVoucher]: {
        type: EndpointType.Post,
        url: '/json/sms/send',
    },
    [ApiEndpoint.DropoutInformation]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/booking/dropout-information',
        converterProvider: async () => {
            const module = await import('proto/Api/Booking/DropoutLink');
            return {
                Request: module.DropoutLinkRequest,
                Response: module.DropoutLinkResponse,
            };
        },
    },
    [ApiEndpoint.ActiveFilterList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/travel/active-filter',
        converterProvider: async () => {
            const module = await import('proto/Api/ActiveFilter');
            return {
                Request: module.ActiveFilterRequest,
                Response: module.ActiveFilterResponse,
            };
        },
    },
    [ApiEndpoint.OccupancySuggestion]: {
        type: EndpointType.Post,
        url: '/json/amzb-suggest',
        baseUrlProvider: defaultBaseUrlProvider,
    },
    [ApiEndpoint.QuestionList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/ask/list',
        converterProvider: async () => {
            const module = await import('proto/Api/Ask/GetQuestionList');
            return {
                Request: module.QuestionListRequest,
                Response: module.QuestionListResponse,
            };
        },
    },
    [ApiEndpoint.QuestionPost]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/ask/post-question',
        converterProvider: async () => {
            const module = await import('proto/Api/Ask/PostQuestion');
            return {
                Request: module.PostQuestionRequest,
                Response: module.PostQuestionResponse,
            };
        },
    },
    [ApiEndpoint.VoteAnswer]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/ask/vote-answer',
        converterProvider: async () => {
            const module = await import('proto/Api/Ask/VoteAnswer');
            return {
                Request: module.VoteAnswerRequest,
                Response: module.VoteAnswerResponse,
            };
        },
    },
    [ApiEndpoint.Servicecode]: {
        type: EndpointType.Post,
        url: '/json/servicecode',
    },
    [ApiEndpoint.ServiceDataCmx]: {
        type: EndpointType.Post,
        url: '/map/service/cmx',
        baseUrlProvider: defaultBaseUrlProvider,
    },
    [ApiEndpoint.ServiceDataHolidayHome]: {
        type: EndpointType.Post,
        url: '/map/service/holidayhome',
        baseUrlProvider: defaultBaseUrlProvider,
    },
    [ApiEndpoint.ServiceDataHotel]: {
        type: EndpointType.Post,
        url: '/map/service',
        baseUrlProvider: defaultBaseUrlProvider,
    },
    [ApiEndpoint.ReportAnswer]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/ask/report',
        converterProvider: async () => {
            const module = await import('proto/Api/Ask/Report');
            return {
                Request: module.ReportRequest,
                Response: module.ReportResponse,
            };
        },
    },
    [ApiEndpoint.GroupList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/community/get-group-list',
        queryOptions: {
            staleTime: 3_600_000_000,
            cacheTime: 3_600_000,
        },
        converterProvider: async () => {
            const module = await import('proto/Api/Community/GetGroupList');
            return {
                Request: module.GroupListRequest,
                Response: module.GroupListResponse,
            };
        },
    },
    [ApiEndpoint.HotelCustomerRating]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/hotel/customer-rating-list',
        converterProvider: async () => {
            const module = await import('proto/Api/Hotel/CustomerRating');
            return {
                Request: module.HotelCustomerRatingRequest,
                Response: module.HotelCustomerRatingResponse,
            };
        },
    },
    [ApiEndpoint.HotelMedia]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/hotel/media',
        queryOptions: {
            staleTime: 3_600_000,
        },
        converterProvider: async () => {
            const module = await import('proto/Api/Hotel/HotelMedia');
            return {
                Request: module.HotelMediaRequest,
                Response: module.HotelMediaResponse,
            };
        },
    },
    [ApiEndpoint.HotelDetails]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/hotel/details',
        converterProvider: async () => {
            const module = await import('proto/Api/Hotel/HotelDetails');
            return {
                Request: module.HotelDetailsRequest,
                Response: module.HotelDetailsResponse,
            };
        },
    },
    [ApiEndpoint.KiFilter]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/ki-filter',
        timeout: 40,
        converterProvider: async () => {
            const module = await import('proto/Api/Llm/KiFilter');
            return {
                Request: module.KiFilterRequest,
                Response: module.KiFilterResponse,
            };
        },
    },
    [ApiEndpoint.IndividualSpot]: {
        type: EndpointType.Post,
        url: '/suche/json/spot-widgets',
    },
    [ApiEndpoint.GetVacancy]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/vacancy/get',
        converterProvider: async () => {
            const module = await import('proto/Api/Vacancy/VacancyGet');
            return {
                Request: module.VacancyGetRequest,
                Response: module.VacancyGetResponse,
            };
        },
    },
    [ApiEndpoint.UserPost]: {
        type: EndpointType.Post,
        url: '/anmelden',
    },
    [ApiEndpoint.CreateBooking]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/vacancy/process',
        converterProvider: async () => {
            const module = await import('proto/Api/Vacancy/VacancyProcess');
            return {
                Request: module.VacancyProcessRequest,
                Response: module.VacancyProcessResponse,
            };
        },
    },
    [ApiEndpoint.UnifiedLoginRequired]: {
        type: EndpointType.Get,
        url: '/json/uli-required',
    },
    [ApiEndpoint.ChatBot]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/travel/chat-bot',
        converterProvider: async () => {
            const module = await import('proto/Api/ChatBot');
            return {
                Request: module.ChatBotRequest,
                Response: module.ChatBotResponse,
            };
        },
    },
    [ApiEndpoint.SaveBooking]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/booking/update',
        converterProvider: async () => {
            const module = await import('proto/Api/Booking/SaveBooking');
            return {
                Request: module.SaveBookingRequest,
                Response: module.SaveBookingResponse,
            };
        },
    },
    [ApiEndpoint.TourOperatorTeaser]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/tour-operator/teaser-list',
        converterProvider: async () => {
            const module = await import('proto/Api/TourOperator/Teaser');
            return {
                Request: module.TeaserRequest,
                Response: module.TeaserResponse,
            };
        },
    },
    [ApiEndpoint.CalculationSample]: {
        type: EndpointType.Get,
        url: '/json/calculation-sample',
        baseUrlProvider: defaultBaseUrlProvider,
        queryOptions: {
            staleTime: 3_600_000_000,
            cacheTime: 3_600_000,
        },
    },
    [ApiEndpoint.AirportAutocompleter]: {
        type: EndpointType.Get,
        url: '/autocompleter-airports',
        baseUrlProvider: baseLocationProvider,
    },
    [ApiEndpoint.DestinationAutoCompleter]: {
        type: EndpointType.Get,
        url: '/autocompleter',
        baseUrlProvider: baseLocationProvider,
    },
    [ApiEndpoint.DestinationAutoCompleterDestination]: {
        type: EndpointType.Get,
        url: '/autocompleter-destination',
        baseUrlProvider: baseLocationProvider,
    },
    [ApiEndpoint.GetShortUrl]: {
        type: EndpointType.Post,
        url: '/json/getshorturl',
        baseUrlProvider: baseLocationProvider,
    },
    [ApiEndpoint.BookingUpsellingList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/booking/upsell/list',
        converterProvider: async () => {
            const module = await import('proto/Api/Booking/Upselling');
            return {
                Request: module.UpsellingListRequest,
                Response: module.UpsellingListResponse,
            };
        },
    },
    [ApiEndpoint.BookingSelectUpselling]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/booking/upsell/select',
        converterProvider: async () => {
            const module = await import('proto/Api/Booking/Upselling');
            return {
                Request: module.UpsellingSelectRequest,
                Response: module.UpsellingSelectResponse,
            };
        },
    },
    [ApiEndpoint.HotelRecommendationList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/hotel/recommendation-list',
        queryOptions: {
            staleTime: 20_000_000, //after this time, data will be null-> renders empty container, fetch new data, if rerendering would be neccessary
            cacheTime: 20_000,
        },
        converterProvider: async () => {
            const module = await import('proto/Api/Hotel/RecommendationList');
            return {
                Request: module.RecommendationListRequest,
                Response: module.RecommendationListResponse,
            };
        },
    },
    [ApiEndpoint.NewsList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/news-list',
        converterProvider: async () => {
            const module = await import('proto/Api/NewsList');
            return {
                Request: module.NewsListRequest,
                Response: module.NewsListResponse,
            };
        },
    },
    [ApiEndpoint.PopularHotelList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/hotel/popular-list',
        converterProvider: async () => {
            const module = await import('proto/Api/Hotel/PopularList');
            return {
                Request: module.PopularListRequest,
                Response: module.PopularListResponse,
            };
        },
    },
    [ApiEndpoint.PopularRegionList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/region/popular-list',
        converterProvider: async () => {
            const module = await import('proto/Api/Region/PopularList');
            return {
                Request: module.PopularListRequest,
                Response: module.PopularListResponse,
            };
        },
    },
    [ApiEndpoint.BlogList]: {
        type: EndpointType.Rpc,
        url: '/rpc/desktop/travel/blog-list',
        converterProvider: async () => {
            const module = await import('proto/Api/Blog');
            return {
                Request: module.BlogRequest,
                Response: module.BlogResponse,
            };
        },
    },
};
